import { RefObject, useEffect } from 'react';

const MD_QUERY = '(min-width: 768px)';
const COMPACT_ATTR = 'data-header-compact';

function readPxVar(name: string): number {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
}

export function useCompactHeader(
    rootRef: RefObject<HTMLElement | null>,
    sentinelRef: RefObject<HTMLElement | null>,
    filterRef: RefObject<HTMLElement | null>,
): void {
    useEffect(() => {
        const html = document.documentElement;
        const mql = window.matchMedia(MD_QUERY);
        const navbar = document.querySelector<HTMLElement>('[data-app-navbar]');
        let frame = 0;

        const update = (): void => {
            frame = 0;
            const root = rootRef.current;
            const sentinel = sentinelRef.current;
            const filter = filterRef.current;

            if (mql.matches || !root || !sentinel || !filter) {
                html.removeAttribute(COMPACT_ATTR);
                return;
            }

            const topbarHeight = readPxVar('--mobile-topbar-height');
            const navbarHeight = readPxVar('--mobile-navbar-height');

            const reached = sentinel.getBoundingClientRect().top <= topbarHeight + navbarHeight;
            const passed = root.getBoundingClientRect().bottom <= navbarHeight + filter.offsetHeight;

            html.toggleAttribute(COMPACT_ATTR, reached && !passed);
        };

        const schedule = (): void => {
            if (!frame) {
                frame = requestAnimationFrame(update);
            }
        };

        const resizeObserver = new ResizeObserver(() => {
            if (navbar) {
                html.style.setProperty('--mobile-navbar-height', `${navbar.offsetHeight}px`);
            }
            schedule();
        });
        if (navbar) {
            resizeObserver.observe(navbar);
        }

        schedule();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            html.removeAttribute(COMPACT_ATTR);
        };
    }, [rootRef, sentinelRef, filterRef]);
}
